import { createStore } from "solid-js/store";
import type { Accessor } from "solid-js";
import { createMemo } from "solid-js";
import type { ColumnSort, OnChangeFn, SortingState } from "@tanstack/solid-table";
import { isNil } from "lodash-es";
import { makePersisted } from "@solid-primitives/storage";
import type { Filter } from "../pages/customer/form/filter";

export type FilterStore = {
  all: () => Filter[];
  add: (filter: Filter) => void;
  upsert: (filter: Filter) => void;
  remove: (id: string) => void;
};

export type ViewConfigurationStore = {
  sorting: Accessor<ColumnSort[]>;
  setSorting: OnChangeFn<SortingState>;
  columnOrder: Accessor<string[]>;
  setColumnOrder: OnChangeFn<string[]>;
  columnVisibility: Accessor<Record<string, boolean>>;
  setColumnVisibility: OnChangeFn<Record<string, boolean>>;
  filterStore: FilterStore;
};

export type ViewConfigurationStoreState = {
  sorting: SortingState;
  columnOrder: string[];
  columnVisibility: Record<string, boolean>;
  filters: Filter[];
};
export const createViewConfigurationStore = (
  defaultSorting: SortingState,
  defaultColumnOrder: string[],
  defaultColumnVisibility: Record<string, boolean>,
  defaultFilters: Filter[],
  persistKey?: string,
  serializeConfigurationStore?: (value: ViewConfigurationStoreState) => string,
  deserializeConfigurationStore?: (value: string) => ViewConfigurationStoreState,
): ViewConfigurationStore => {
  const viewConfigurationStore = createStore<ViewConfigurationStoreState>({
    sorting: defaultSorting,
    columnOrder: defaultColumnOrder,
    columnVisibility: defaultColumnVisibility,
    filters: defaultFilters,
  });

  const [store, setStore] = isNil(persistKey)
    ? viewConfigurationStore
    : makePersisted(viewConfigurationStore, {
        name: persistKey,
        serialize: serializeConfigurationStore,
        deserialize: deserializeConfigurationStore,
      });

  const sorting = createMemo(() => [...store.sorting]);

  const setSorting: OnChangeFn<SortingState> = (updaterOrValue) => setStore("sorting", updaterOrValue);

  const columnOrder = createMemo(() => [...store.columnOrder]);

  const setColumnOrder: OnChangeFn<string[]> = (updaterOrValue) => {
    setStore("columnOrder", updaterOrValue);
  };

  const columnVisibility = createMemo(() => ({ ...store.columnVisibility }));

  const setColumnVisibility: OnChangeFn<Record<string, boolean>> = (updaterOrValue) =>
    setStore("columnVisibility", updaterOrValue);

  const all = () => store.filters;

  const add = (filter: Filter) => {
    setStore("filters", (l) => [...l, filter]);
  };

  const upsert = (filter: Filter) =>
    setStore("filters", (l) => {
      const idx = l.findIndex((f) => f.id === filter.id);
      return idx === -1 ? [...l, filter] : [...l.slice(0, idx), filter, ...l.slice(idx + 1)];
    });

  const remove = (id: string) => setStore("filters", (l) => l.filter((f) => f.id !== id));

  return {
    sorting,
    setSorting,
    columnOrder,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
    filterStore: {
      all,
      add,
      upsert,
      remove,
    },
  };
};
